import { ChangeEvent, DependencyList, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { isFunction, isUndefined } from 'lodash-es';
import { CheckboxChangeEventTarget } from '../components/CheckboxExclusiveAli';

export interface UseControllableStateProps<T> {
  value?: T;
  defaultValue?: T | (() => T);
  onChange?: (value: T) => void;
  shouldUpdate?: (prev: T, next: T) => boolean;

}
type CheckboxActionType = 'unchecked' | 'checked';

interface CheckboxState {
  checked: boolean;
  indeterminate: boolean;
}

// 针对函数引用，通过ref来缓存地址，保证引用不变
export function useCallbackRef<T extends (...args: any[]) => any>(callback: T | undefined, deps: DependencyList = []) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(((...args) => callbackRef.current?.(...args)) as T, deps);
}
// 针对业务组件可以兼容受控与非受控的，参照ahooks中useControllableValue
export function useControllableState<T>(props: UseControllableStateProps<T>) {
  const { value: valueProp, defaultValue, onChange, shouldUpdate = (prev, next) => prev !== next } = props;
  // 缓存父级传下来的fn，防止本组件嵌套其他组件，引起不必要的开销
  const onChangeProp = useCallbackRef(onChange);
  const shouldUpdateProp = useCallbackRef(shouldUpdate);
  // 获取外部传入的value
  const [uncontrolledState, setUncontrolledState] = useState(defaultValue as T);
  const controlled = valueProp !== undefined;
  const value = controlled ? valueProp : uncontrolledState;
  // 更新视图
  const setValue = useCallbackRef(
    (next: SetStateAction<T>) => {
      const setter = next as (prevState?: T) => T;
      const nextValue = typeof next === 'function' ? setter(value) : next;

      if (!shouldUpdateProp(value, nextValue)) {
        return;
      }
      // 非受控，更新state
      if (!controlled) {
        setUncontrolledState(nextValue);
      }

      onChangeProp(nextValue);
    },
    [controlled, onChangeProp, value, shouldUpdateProp],
  );

  return [value, setValue] as [T, Dispatch<SetStateAction<T>>];
}
function normalizeCheckedState(state: CheckboxState) {
  if (isUndefined(state.checked) && isUndefined(state.indeterminate)) {
    return;
  }
  return state;
}

function getCheckboxState(action: CheckboxActionType) {
  if (action === 'checked') {
    return { checked: true, indeterminate: false };
  }
  if (action === 'unchecked') {
    return { checked: false, indeterminate: false };
  }
}

export function useCheckbox(props: Partial<CheckboxChangeEventTarget>) {
  const {
    value,
    checked: checkedProp,
    defaultChecked = false,
    indeterminate: indeterminateProp,
    defaultIndeterminate = false,
    disabled = false,
    name,
    onChange,
    ...htmlProps
  } = props;
  const checkedState = normalizeCheckedState({
    checked: checkedProp,
    indeterminate: true,
  });

  const defaultCheckedState = normalizeCheckedState({
    checked: defaultChecked,
    indeterminate: defaultIndeterminate,
  });

  const [checkedStateValue, updateCheckedStateValue] = useControllableState({
    value: checkedState,
    defaultValue: defaultCheckedState,
    onChange(nextValue) {
      isFunction(onChange) && onChange(nextValue.checked, nextValue);
    },
    name: 'Checkbox',
  });

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      const action: CheckboxActionType = checkedStateValue.checked ? 'unchecked' : 'checked';
      const nextState = getCheckboxState(action);
      updateCheckedStateValue(nextState);
    },
    [checkedStateValue.checked, updateCheckedStateValue, disabled],
  );

  const getInputProps = useCallback(
    (props) => {
      return {
        type: 'checkbox',
        name,
        value,
        onChange: handleChange,
        checked: checkedStateValue.checked,
        // ref: forwardedRef,
        ...props,
      };
    },
    [name, value, handleChange, checkedStateValue.checked],
  );
  return {
    state: {
      ...checkedStateValue,
    },
    getInputProps,
    htmlProps,
  };
}
